import { useMemo, useState } from 'react';
import { agentTitle, type AgentRow, type Api, type NumberPlanInput, type PhoneNumber, type RefuseStyle, type TelephonyView } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { DangerButton, Field, Gate, ReadOnlyNote } from '../ui';
import { dayName, fromAgentHours, groupDays, weekOrder, type Week, type Win } from '../plan';
import { browserZone, zones } from './ActionsPanel';
import { errorText } from './AgentSettings';

/** The panel language (App sets <html lang>); Intl gives day names and the week start in it. */
const lang = (): string => (typeof document !== 'undefined' && document.documentElement.lang) || 'en';
const ALL_DAY: Win = { start: '00:00', end: '24:00' };
const isAllDay = (ws: Win[] | undefined) => ws?.length === 1 && ws[0]!.start === '00:00' && ws[0]!.end === '24:00';
const mins = (hm: string) => (hm === '24:00' ? 1440 : Number(hm.slice(0, 2)) * 60 + Number(hm.slice(3, 5)));
/** Windows sorted, each start before its end, no overlap — the same rule the server applies. */
const windowsOk = (ws: Win[]) =>
  [...ws].sort((a, b) => mins(a.start) - mins(b.start)).every((w, i, s) => /^\d\d:\d\d$/u.test(w.start) && mins(w.start) < mins(w.end) && (i === 0 || mins(w.start) >= mins(s[i - 1]!.end)));

/** BRAIN/34 P3: a number's hours in the numbers table — summary, the state now, who answers after hours. */
export const PlanCell = ({ t, n, agents, onEdit }: { t: T; n: PhoneNumber; agents: AgentRow[]; onEdit: () => void }) => {
  const hours = n.plan?.hours ?? null;
  const night = agents.find((a) => a.id === n.plan?.agentId);
  return (
    <div className="plan-cell">
      {!hours ? (
        <span className="hint">{t('numbers.plan.always')}</span>
      ) : (
        <>
          {groupDays(hours.week, lang()).map((g) => (
            <div key={g.days} className="small"><span>{g.days}</span> <bdi dir="ltr" className="mono">{g.hours}</bdi></div>
          ))}
          {n.planNow && <span className={`pill ${n.planNow.state === 'open' ? 'published' : 'muted'}`}>{t(`numbers.plan.now.${n.planNow.state}`)}</span>}
          <div className="hint">
            {n.plan?.mode === 'agent'
              ? t('numbers.plan.after.agent', { agent: night ? agentTitle(night) : '—' })
              : n.plan?.mode === 'refuse'
              ? `${t('numbers.plan.after.refuse')} · ${t(`plan.refuse.${n.plan.refuseStyle ?? 'ring'}`)}`
              : t(`numbers.plan.after.${n.plan?.mode ?? 'same'}`)}
          </div>
          {n.plan?.mode === 'refuse' && (n.refused7d ?? 0) > 0 && <div className="hint">{t('plan.refused', { today: n.refusedToday ?? 0, week: n.refused7d ?? 0 })}</div>}
        </>
      )}
      {!n.inboundAgentId ? (
        <div className="hint">{t('numbers.plan.needAgent')}</div>
      ) : (
        <Gate need={PERM.lines}>
          <button type="button" className="link" onClick={onEdit}>{hours ? t('plan.edit') : t('numbers.plan.add')}</button>
        </Gate>
      )}
    </div>
  );
};

/** BRAIN/34 P3 (ADR-073): edit a number's opening hours and who answers outside them. */
export const NumberPlanCard = ({ api, t, view, number, agents, onSaved, onCancel }: {
  api: Api; t: T; view: TelephonyView; number: PhoneNumber; agents: AgentRow[]; onSaved: () => void; onCancel: () => void;
}) => {
  const canEdit = useCan()(PERM.lines);
  const all = useMemo(zones, []);
  const order = useMemo(() => weekOrder(lang()), []);
  const dayAgent = number.inboundAgentId ? view.agentHours?.[number.inboundAgentId] : undefined;
  const [limited, setLimited] = useState(number.plan?.hours !== null && number.plan?.hours !== undefined);
  const [timezone, setTimezone] = useState(number.plan?.hours?.timezone ?? dayAgent?.timezone ?? browserZone());
  const [week, setWeek] = useState<Week>(number.plan?.hours?.week ?? { '1': [{ start: '09:00', end: '18:00' }], '2': [{ start: '09:00', end: '18:00' }], '3': [{ start: '09:00', end: '18:00' }], '4': [{ start: '09:00', end: '18:00' }], '5': [{ start: '09:00', end: '18:00' }] });
  const [mode, setMode] = useState<'same' | 'agent' | 'refuse'>(number.plan?.mode ?? 'same');
  const [agentId, setAgentId] = useState<string | null>(number.plan?.agentId ?? null);
  const [style, setStyle] = useState<RefuseStyle>(number.plan?.refuseStyle ?? 'ring');
  const [message, setMessage] = useState(number.plan?.closedMessage ?? '');
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const setDay = (d: number, ws: Win[] | undefined) => setWeek({ ...week, [String(d)]: ws && ws.length ? ws : undefined });
  const open = order.filter((d) => (week[String(d)] ?? []).length > 0);
  const invalid = limited && (open.length === 0 ? 'empty' : open.some((d) => !windowsOk(week[String(d)]!)) ? 'window' : null);
  const nightAgents = agents.filter((a) => a.publishedPackageId && a.id !== number.inboundAgentId);
  const chosen = view.agentHours?.[agentId ?? ''];
  const save = async (plan: NumberPlanInput | null) => {
    setBusy(true);
    setError(undefined);
    try {
      await api.setNumberPlan(number.id, plan);
      onSaved();
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const now = number.planNow;
  const answer = (() => {
    if (!now) return '';
    if (now.state === 'open' || number.plan?.mode === 'same') return t('plan.answer.agent', { agent: agentTitle(agents.find((a) => a.id === number.inboundAgentId) ?? { name: '—' }) });
    if (number.plan?.mode === 'refuse') return t('plan.answer.nobody');
    return t('plan.answer.agent', { agent: agentTitle(agents.find((a) => a.id === number.plan?.agentId) ?? { name: '—' }) });
  })();

  return (
    <div className="card line-form plan-card">
      <h3>{t('plan.title', { number: number.e164 })}</h3>
      <ReadOnlyNote t={t} need={PERM.lines} />
      <p className="hint">{t('plan.scopeNote')}</p>
      <fieldset disabled={!canEdit || busy} className="plan-fields">
        <div className="choice-row">
          {([false, true] as const).map((l) => (
            <label key={String(l)} className={`choice ${limited === l ? 'on' : ''}`}>
              <input type="radio" name="plan-mode" checked={limited === l} onChange={() => setLimited(l)} />
              <span>{t(l ? 'plan.mode.limited' : 'plan.mode.always')}{!l && <small>{t('plan.mode.always.hint')}</small>}</span>
            </label>
          ))}
        </div>
        {limited && (
          <>
            <Field label={t('actions.timezone')}>
              <div className="row">
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)} aria-label={t('actions.timezone')} style={{ maxWidth: 320 }}>
                  {!all.includes(timezone) && <option value={timezone}>{timezone}</option>}
                  {all.map((z) => <option key={z} value={z}>{z.replaceAll('_', ' ')}</option>)}
                </select>
                {timezone !== browserZone() && <button type="button" className="ghost" onClick={() => setTimezone(browserZone())}>{t('actions.timezone.browser', { zone: browserZone() })}</button>}
                {dayAgent?.timezone && dayAgent.timezone !== timezone && <button type="button" className="ghost" onClick={() => setTimezone(dayAgent.timezone!)}>{t('plan.timezone.agent', { zone: dayAgent.timezone })}</button>}
              </div>
            </Field>
            {dayAgent?.businessHours && (
              <button type="button" className="ghost" onClick={() => setWeek(fromAgentHours(dayAgent.businessHours!))}>{t('plan.copyAgent')}</button>
            )}
            <div className="plan-week">
              {order.map((d) => {
                const ws = week[String(d)] ?? [];
                const allDay = isAllDay(ws);
                return (
                  <div key={d} className="plan-day">
                    <label className="row plan-day-name">
                      <input type="checkbox" checked={ws.length > 0} onChange={(e) => setDay(d, e.target.checked ? [{ start: '09:00', end: '18:00' }] : undefined)} />
                      <span>{dayName(lang(), d, 'long')}</span>
                    </label>
                    {ws.length === 0 && <span className="hint">{t('plan.day.closed')}</span>}
                    {ws.length > 0 && (
                      <div className="plan-windows">
                        {!allDay &&
                          ws.map((w, i) => (
                            <span key={i} className="row plan-window">
                              <input type="time" dir="ltr" value={w.start} aria-label={`${dayName(lang(), d, 'long')} ${i + 1}`} onChange={(e) => setDay(d, ws.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                              <span>–</span>
                              <input type="time" dir="ltr" value={w.end === '24:00' ? '23:59' : w.end} aria-label={`${dayName(lang(), d, 'long')} ${i + 1}`} onChange={(e) => setDay(d, ws.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                              {i === 1 && <button type="button" className="link" onClick={() => setDay(d, [ws[0]!])}>{t('plan.window.remove')}</button>}
                            </span>
                          ))}
                        {!allDay && ws.length === 1 && <button type="button" className="link" onClick={() => setDay(d, [...ws, { start: '13:30', end: '18:00' }])}>{t('plan.window.add')}</button>}
                        <label className="row small"><input type="checkbox" checked={allDay} onChange={(e) => setDay(d, e.target.checked ? [ALL_DAY] : [{ start: '09:00', end: '18:00' }])} /> {t('plan.day.allDay')}</label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {open.length > 1 && <button type="button" className="ghost" onClick={() => setWeek(Object.fromEntries(open.map((d) => [String(d), week[String(open[0]!)]!])))}>{t('plan.applyAll')}</button>}
            <p className="hint">{t('plan.overnight.hint')}</p>
            {invalid && <div className="banner warn small">{t(`plan.invalid.${invalid}`)}</div>}
            <p className="hint">{t('plan.closedDates.workspaceNote')}</p>
            <h4 className="section-gap">{t('plan.after')}</h4>
            <div className="choice-row vertical">
              {(['same', 'agent', 'refuse'] as const).map((m) => (
                <label key={m} className={`choice ${mode === m ? 'on' : ''}`}>
                  <input type="radio" name="after-hours" checked={mode === m} onChange={() => setMode(m)} />
                  <span>{t(`plan.after.${m}`)}<small>{t(`plan.after.${m}.hint`)}</small></span>
                </label>
              ))}
            </div>
            {mode === 'refuse' && (
              <div className="plan-refuse">
                <span className="label">{t('plan.refuse.style')}</span>
                {(['ring', 'message', 'reject'] as const).map((x) => (
                  <label key={x} className="row small">
                    <input type="radio" name="refuse-style" checked={style === x} onChange={() => setStyle(x)} />
                    <span>{t(`plan.refuse.${x}`)} <span className="hint">· {t(`plan.refuse.${x}.hint`)}</span></span>
                  </label>
                ))}
                {style === 'message' && (
                  <>
                    <textarea value={message} maxLength={300} rows={3} placeholder={t('plan.refuse.message.placeholder')} aria-label={t('plan.refuse.message')} onChange={(e) => setMessage(e.target.value)} />
                    <span className="hint">{t('plan.refuse.message.note', { n: 300 - message.length })}</span>
                  </>
                )}
              </div>
            )}
            {mode === 'agent' && (
              <>
                <select value={agentId ?? ''} onChange={(e) => setAgentId(e.target.value || null)} aria-label={t('plan.after.agent.pick')}>
                  <option value="">{t('plan.after.agent.pick')}</option>
                  {nightAgents.map((a) => <option key={a.id} value={a.id}>{agentTitle(a)}</option>)}
                </select>
                {agentId && !chosen?.businessHours && <div className="banner warn small">{t('plan.after.agent.noHours')}</div>}
                {agentId && !view.agentNumbers[agentId] && <div className="banner info small">{t('plan.after.agent.noNumber')}</div>}
              </>
            )}
          </>
        )}
      </fieldset>
      {now && (
        <p className="hint section-gap">
          {t('plan.preview', { day: dayName(lang(), now.isoDay, 'long'), time: now.localTime, zone: number.plan?.hours?.timezone ?? '', state: t(`plan.state.${now.state}`), answer })}{' '}
          <span className="muted">{t('plan.preview.note')}</span>
        </p>
      )}
      {number.plan?.mode === 'refuse' && number.plan.refuseStyle === 'message' && (
        <button
          type="button"
          className="ghost"
          disabled={listening}
          onClick={() => {
            setListening(true);
            api
              .closedMessageAudio(number.id)
              .then((b) => {
                const url = URL.createObjectURL(b);
                const a = new Audio(url);
                a.onended = () => URL.revokeObjectURL(url);
                return a.play();
              })
              .catch((e: unknown) => setError(errorText(t, e)))
              .finally(() => setListening(false));
          }}
        >
          ▶ {t('plan.refuse.message.listen')}
        </button>
      )}
      {error && <div className="banner error small">{error}</div>}
      <div className="row-between section-gap">
        <span>{number.plan?.hours && canEdit && <DangerButton t={t} label={t('plan.remove')} onConfirm={() => void save(null)} />}</span>
        <span className="row">
          <button type="button" className="ghost" onClick={onCancel}>{t('settings.discard')}</button>
          {canEdit && (
            <button
              type="button"
              className="primary"
              disabled={busy || Boolean(invalid) || (limited && mode === 'agent' && !agentId) || (limited && mode === 'refuse' && style === 'message' && !message.trim())}
              onClick={() =>
                void save(
                  limited
                    ? { timezone, week, afterHours: { mode, agentId: mode === 'agent' ? agentId : null, ...(mode === 'refuse' ? { refuseStyle: style, closedMessage: style === 'message' ? message.trim() : null } : {}) } }
                    : null,
                )
              }
            >
              {busy ? t('settings.saving') : t('privacy.save')}
            </button>
          )}
        </span>
      </div>
    </div>
  );
};

/** BRAIN/34 P3 (owner 2026-10-13): closed dates of the workspace, for all its numbers (holidays; no national calendar). */
export const ClosedDatesCard = ({ api, t, dates, onSaved }: { api: Api; t: T; dates: string[]; onSaved: () => void }) => {
  const canEdit = useCan()(PERM.lines);
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = dates.filter((d) => d >= today);
  const save = async (next: string[]) => {
    setBusy(true);
    setError(undefined);
    try {
      // Past dates are dropped when saving: they no longer matter and the list stays short.
      await api.setClosedDates(next.filter((d) => d >= today));
      setDay('');
      onSaved();
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const fmt = (d: string) => new Intl.DateTimeFormat(lang(), { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));
  return (
    <div className="card line-form section-gap">
      <h3>{t('plan.closedDates')}</h3>
      <p className="hint">{t('plan.closedDates.hint')}</p>
      <div className="agent-chips">
        {upcoming.length === 0 && <span className="hint">{t('plan.closedDates.empty')}</span>}
        {upcoming.map((d) => (
          <span key={d} className="agent-chip">
            {fmt(d)}
            {canEdit && <button type="button" aria-label={t('settings.remove')} disabled={busy} onClick={() => void save(dates.filter((x) => x !== d))}>×</button>}
          </span>
        ))}
      </div>
      <Gate need={PERM.lines}>
        <div className="inline-form section-gap">
          <input type="date" dir="ltr" min={today} value={day} onChange={(e) => setDay(e.target.value)} aria-label={t('plan.closedDates')} />
          <button type="button" className="secondary" disabled={busy || !day || dates.includes(day)} onClick={() => void save([...dates, day])}>{t('plan.closedDates.add')}</button>
        </div>
      </Gate>
      {error && <div className="banner error small">{error}</div>}
    </div>
  );
};
